import { api, isApiError } from '@/api';
import type { Company, Level } from '../models';

export async function fetchCompany(companyUuid?: string): Promise<Company> {
  return api.get<Company>(companyUuid ? `/v1/company/${companyUuid}` : '/v1/company');
}

export interface UpdateCompanyError {
  type: 'DUPLICATE_TAX_NUMBER' | 'VALIDATION_ERROR' | 'UNKNOWN';
  message: string;
}

export async function updateCompany(company: Company): Promise<Company> {
  try {
    return await api.put<Company>('/v1/company', company);
  } catch (error) {
    if (isApiError(error) && error.status === 409) {
      const updateError: UpdateCompanyError = {
        type: 'DUPLICATE_TAX_NUMBER',
        message: 'Diese Kombination aus Land und Steuernummer existiert bereits.',
      };
      throw updateError;
    }
    const updateError: UpdateCompanyError = {
      type: 'UNKNOWN',
      message: isApiError(error) ? error.message : 'Failed to update company',
    };
    throw updateError;
  }
}

export interface CompanyContactAddress {
  street: string;
  additional_address_line?: string;
  postal_code: string;
  city: string;
  country: string;
}

export interface CompanyContact {
  salutation: number;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  email: string;
  phone_number?: string;
}

export interface CompanyRegistrationRequest {
  country_id: string;
  company_identifier: string;
  tax_number: string;
  company_contact: CompanyContact | null;
  company_name: string;
  address: CompanyContactAddress;
  email: string;
  phone_number?: string;
  parentCompany?: string;
  levelId?: string;
  customerId?: string;
}

export interface RegisterCompanyError {
  type: 'DUPLICATE_TAX_NUMBER' | 'VALIDATION_ERROR' | 'UNKNOWN';
  message: string;
}

export async function updateCompanyLevel(
  companyId: string,
  levelId: string
): Promise<void> {
  await api.put(`/v1/company/${companyId}/level`, { levelId });
}

export async function moveCompany(
  companyId: string,
  targetCompanyId: string
): Promise<void> {
  await api.post('/v1/company/move', { companyId, targetCompanyId });
}

export async function fetchUserLevels(companyId: string): Promise<Level[]> {
  return api.get<Level[]>(`/v1/company/${companyId}/userLevels`);
}

/**
 * Fetches the company levels assigneable by the parent company. These are the possible values the company level of this companyId can be set to.
 * @param companyId The ID of the company to fetch possible levels for.
 * @param locale The language used for localized level names.
 * @returns A promise that resolves to the possible company levels.
 */
export async function fetchPossibleCompanyLevels(
  companyId: string,
  locale: string
): Promise<CompanyLevelsResponse> {
  return api.get<CompanyLevelsResponse>(
    `/v1/companies/${companyId}/parentAssignableLevels`,
    { params: { locale } }
  );
}

interface CompanyLevelsResponse {
  possibleLevels: Level[];
  parentCompany: Level;
}


export interface RegisterCompanyResponse {
  success: boolean;
  company_id?: string;
}

export async function registerCompany(
  request: CompanyRegistrationRequest
): Promise<RegisterCompanyResponse> {
  try {
    return await api.post<RegisterCompanyResponse>(
      '/v1/company/register', request);
  } catch (error) {
    if (isApiError(error) && error.status === 409) {
      const registerError: RegisterCompanyError = {
        type: 'DUPLICATE_TAX_NUMBER',
        message: 'Diese Kombination aus Land und Steuernummer existiert bereits.',
      };
      throw registerError;
    }
    const registerError: RegisterCompanyError = {
      type: 'UNKNOWN',
      message: isApiError(error) ? error.message : 'Failed to register company',
    };
    throw registerError;
  }
}
